import React, { useEffect, useState } from "react";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { isModuleAdmin } from "../../constants/platformModules";
import MasterSection from "../Platform/setup/MasterSection";
import useGlContext from "./gl/glContext";
import { resetAccountCache } from "./gl/AccountPicker";
import { ACCOUNT_TYPES, Badge, DataTable, GlState, LedgerBar, QUALIFIER_LABELS, accountTypeLabel, act, hint, linkBtn } from "./gl/glUi";
import { AppDropdown, EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, inputStyle, pageStyle, tdStyle, thStyle, useLoad } from "../../components/ui";

// Chart of accounts: the values of each segment (accounts, cost centres, business units ...),
// the structure (which segments, required or defaulted), the account combinations created
// so far, and cross-validation rules that stop combinations which make no sense.
const CONTROL = [
  { label: "None", value: "NONE" },
  { label: "Receivables", value: "AR" },
  { label: "Payables", value: "AP" },
  { label: "Inventory", value: "INV" },
  { label: "Tax", value: "TAX" },
  { label: "Bank", value: "BANK" },
];
const QUALIFIER_OPTIONS = Object.entries(QUALIFIER_LABELS).map(([value, label]) => ({ label, value }));

/* ------------------------------ segment values ------------------------------ */

function Hierarchy({ segment, parent, onHide }) {
  const { data, loading, error, reload } = useLoad(() => finService.listHierarchy(segment.value_set_id), [segment.value_set_id]);
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const rows = (data || []).filter((r) => r.parent_value === parent.value);
  const add = async () => {
    if (!(await act(() => finService.addHierarchyRange(segment.value_set_id, { parent_value: parent.value, child_low: low, child_high: high || low })))) return;
    setLow("");
    setHigh("");
    reload();
  };
  return (
    <SlidePanel show onHide={onHide} title={`${parent.value} – ${parent.description}`} width={520}>
      <div style={{ ...hint, marginBottom: 12 }}>The values this summary value rolls up. Reports total these ranges.</div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length ? (
          <DataTable
            rows={rows}
            minWidth={320}
            columns={[
              { label: "From", render: (r) => r.child_low },
              { label: "To", render: (r) => r.child_high },
              { label: "", render: (r) => <button type="button" style={linkBtn} onClick={async () => (await act(() => finService.removeHierarchyRange(r.id))) && reload()}>Remove</button> },
            ]}
          />
        ) : (
          <EmptyState title="No child ranges yet" />
        )}
      </LoadState>
      <div className="d-flex gap-2 align-items-end mt-3">
        <Field label="From" htmlFor="h-low" style={{ flex: 1 }}>
          <input id="h-low" className="form-control" style={inputStyle} value={low} onChange={(e) => setLow(e.target.value.toUpperCase())} />
        </Field>
        <Field label="To" htmlFor="h-high" style={{ flex: 1 }}>
          <input id="h-high" className="form-control" style={inputStyle} value={high} onChange={(e) => setHigh(e.target.value.toUpperCase())} />
        </Field>
        <PrimaryButton style={{ marginBottom: 16 }} disabled={!low} onClick={add}>
          Add
        </PrimaryButton>
      </div>
    </SlidePanel>
  );
}

function SegmentValues({ structure, admin }) {
  const [qualifier, setQualifier] = useState("ACCOUNT");
  const [tree, setTree] = useState(null);
  const segment = structure.segments.find((s) => s.qualifier === qualifier) || structure.segments[0];
  const isAccount = segment.qualifier === "ACCOUNT";
  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {structure.segments.map((s) => (
          <button
            key={s.qualifier}
            type="button"
            className={`btn btn-sm ${s.qualifier === segment.qualifier ? "btn-danger" : "btn-outline-secondary"}`}
            style={{ borderRadius: 16, fontWeight: 600 }}
            onClick={() => setQualifier(s.qualifier)}
          >
            {s.name}
          </button>
        ))}
      </div>
      <div style={{ ...hint, marginBottom: 12 }}>
        {segment.name} values ({segment.value_set_name}). {segment.is_required ? "Required on every account." : `Optional: left blank it takes ${segment.default_value || "its default"}.`}
      </div>
      <MasterSection
        key={segment.qualifier}
        noun={`${segment.name.toLowerCase()} value`}
        canEdit={admin}
        deps={[segment.value_set_id]}
        load={() => finService.listMaster("segment-values", { value_set_id: segment.value_set_id })}
        save={(id, data) => {
          resetAccountCache();
          return finService.saveMaster("segment-values", id, id ? data : { ...data, value_set_id: segment.value_set_id });
        }}
        columns={[
          ["Value", (r) => <span style={{ fontWeight: r.is_parent ? 700 : 400 }}>{r.value}</span>],
          ["Description", (r) => r.description],
          ...(isAccount ? [["Type", (r) => accountTypeLabel(r.account_type)], ["Control", (r) => (r.control_account && r.control_account !== "NONE" ? r.control_account : "—")]] : []),
          ["Posting", (r) => (r.is_parent ? "Summary" : r.allow_posting ? "Yes" : "No")],
        ]}
        fieldKeys={["description", "is_parent", "account_type", "allow_posting", "control_account", "enabled_from", "enabled_to"]}
        lockedOnEdit={["value"]}
        defaults={{ allow_posting: true, control_account: "NONE" }}
        toForm={(r) => ({ ...r, is_parent: !!r.is_parent, allow_posting: !!r.allow_posting })}
        extraActions={(r) =>
          r.is_parent ? (
            <>
              <button type="button" style={linkBtn} onClick={() => setTree(r)}>
                Children
              </button>{" "}
            </>
          ) : null
        }
        fields={(v) => [
          { key: "value", label: "Value", required: true, max: 25, hint: "Letters and digits. Cannot be changed once created." },
          { key: "description", label: "Description", required: true, max: 150 },
          ...(isAccount ? [{ key: "account_type", label: "Account type", type: "select", required: !v.is_parent, options: ACCOUNT_TYPES }] : []),
          { key: "is_parent", label: "Summary (parent) value: totals other values, never posted to", type: "checkbox" },
          ...(v.is_parent ? [] : [{ key: "allow_posting", label: "Allow posting", type: "checkbox" }]),
          ...(isAccount && !v.is_parent ? [{ key: "control_account", label: "Control account (posted by its subledger only)", type: "select", options: CONTROL }] : []),
          { key: "enabled_from", label: "Enabled from", type: "date" },
          { key: "enabled_to", label: "Enabled to", type: "date" },
        ]}
      />
      {tree ? <Hierarchy segment={segment} parent={tree} onHide={() => setTree(null)} /> : null}
    </>
  );
}

/* ------------------------------ structure ------------------------------ */

function Structure({ structure, admin, onSaved }) {
  const [segs, setSegs] = useState(structure.segments);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSegs(structure.segments), [structure]);
  const set = (i, patch) => setSegs(segs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const save = async () => {
    setBusy(true);
    const res = await act(() =>
      finService.saveStructure(structure.id, {
        name: structure.name,
        delimiter: structure.delimiter,
        segments: segs.map((s) => ({ seq: s.seq, qualifier: s.qualifier, name: s.name, value_set_id: s.value_set_id, is_required: s.is_required ? 1 : 0, default_value: s.default_value || null })),
      })
    );
    setBusy(false);
    if (res) onSaved();
  };
  return (
    <>
      <div style={{ ...hint, marginBottom: 12 }}>
        Accounts are written {structure.segments.map((s) => s.name).join(structure.delimiter)}. Company and Account are always required; an optional segment left blank takes its default. Once journals use the chart only names and defaults can change.
      </div>
      <div style={{ ...cardStyle, overflowX: "auto" }}>
        <table className="table mb-0" style={{ minWidth: 640 }}>
          <thead>
            <tr>
              {["#", "Segment", "Name", "List of values", "Required", "Default"].map((h) => (
                <th key={h} style={thStyle}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {segs.map((s, i) => (
              <tr key={s.qualifier}>
                <td style={tdStyle}>{s.seq}</td>
                <td style={tdStyle}>{QUALIFIER_LABELS[s.qualifier]}</td>
                <td style={tdStyle}>
                  <input className="form-control" style={{ ...inputStyle, height: 36 }} aria-label={`${s.qualifier} name`} value={s.name} disabled={!admin} onChange={(e) => set(i, { name: e.target.value })} />
                </td>
                <td style={tdStyle}>{s.value_set_code}</td>
                <td style={tdStyle}>
                  <input type="checkbox" className="form-check-input" aria-label={`${s.qualifier} required`} checked={!!s.is_required} disabled={!admin || ["COMPANY", "ACCOUNT"].includes(s.qualifier)} onChange={(e) => set(i, { is_required: e.target.checked })} />
                </td>
                <td style={tdStyle}>
                  <input className="form-control" style={{ ...inputStyle, height: 36, width: 110 }} aria-label={`${s.qualifier} default`} value={s.default_value || ""} disabled={!admin} onChange={(e) => set(i, { default_value: e.target.value.toUpperCase() })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {admin ? (
        <div className="mt-3">
          <PrimaryButton onClick={save} disabled={busy}>
            Save structure
          </PrimaryButton>
        </div>
      ) : null}
    </>
  );
}

/* ------------------------------ combinations ------------------------------ */

function Combinations({ structure, admin }) {
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => finService.listCombinations(structure.id, { q: q || undefined }), [structure.code, q]);
  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 280 }} placeholder="Search, e.g. 4000" aria-label="Search combinations" defaultValue={q} onKeyDown={(e) => e.key === "Enter" && setQ(e.target.value.trim())} />
        <span style={hint}>Combinations are created the first time an account is used. Disable one to stop new postings to it.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No combinations" text="They appear as journals are entered." />}
          columns={[
            { label: "Account", render: (r) => <span style={{ fontFamily: "monospace" }}>{r.concatenated}</span> },
            { label: "Type", render: (r) => accountTypeLabel(r.account_type) },
            { label: "Control", render: (r) => (r.control_account && r.control_account !== "NONE" ? r.control_account : "—") },
            { label: "Status", render: (r) => <Badge status={r.enabled ? "ACTIVE" : "INACTIVE"} /> },
            {
              label: "",
              render: (r) =>
                admin ? (
                  <button type="button" style={linkBtn} onClick={async () => (await act(() => finService.setCombinationEnabled(r.id, !r.enabled))) && reload()}>
                    {r.enabled ? "Disable" : "Enable"}
                  </button>
                ) : null,
            },
          ]}
        />
      </LoadState>
    </>
  );
}

/* ------------------------------ cross-validation ------------------------------ */

const blankRange = () => ({ qualifier: "COMPANY", low: "", high: "" });

function Ranges({ label, rows, onChange }) {
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="mb-3">
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {rows.map((r, i) => (
        <div key={i} className="d-flex gap-2 mb-2">
          <div style={{ width: 170 }}>
            <AppDropdown id={`${label}-${i}`} value={r.qualifier} options={QUALIFIER_OPTIONS} onChange={(e) => set(i, { qualifier: e.value })} />
          </div>
          <input className="form-control" style={{ ...inputStyle, width: 110 }} placeholder="From" aria-label={`${label} ${i + 1} from`} value={r.low} onChange={(e) => set(i, { low: e.target.value.toUpperCase() })} />
          <input className="form-control" style={{ ...inputStyle, width: 110 }} placeholder="To" aria-label={`${label} ${i + 1} to`} value={r.high} onChange={(e) => set(i, { high: e.target.value.toUpperCase() })} />
          {rows.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onChange([...rows, blankRange()])}>
        Add range
      </button>
    </div>
  );
}

function CvrEditor({ structure, rule, onHide, onDone }) {
  const [form, setForm] = useState(rule ? { ...rule } : { name: "", error_message: "", conditions: [blankRange()], validations: [blankRange()], status: "ACTIVE" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const res = await act(() => finService.saveCvr(structure.id, rule ? rule.id : null, form));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title={rule ? "Edit rule" : "New cross-validation rule"}
      width={620}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <Field label="Name" required htmlFor="cvr-name">
        <input id="cvr-name" className="form-control" style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Message when broken" required htmlFor="cvr-msg">
        <input id="cvr-msg" className="form-control" style={inputStyle} value={form.error_message} onChange={(e) => setForm({ ...form, error_message: e.target.value })} />
      </Field>
      <Ranges label="When the account has" rows={form.conditions} onChange={(conditions) => setForm({ ...form, conditions })} />
      <Ranges label="It must also have" rows={form.validations} onChange={(validations) => setForm({ ...form, validations })} />
      <div style={hint}>Example: when Account is 4000–4999, Cost centre must be 100–299 (revenue only in sales cost centres).</div>
    </SlidePanel>
  );
}

function CrossValidation({ structure, admin }) {
  const { data, loading, error, reload } = useLoad(() => finService.listCvrs(structure.id), [structure.code]);
  const [edit, setEdit] = useState(undefined);
  const fmt = (list) => list.map((r) => `${QUALIFIER_LABELS[r.qualifier]} ${r.low === r.high ? r.low : `${r.low}–${r.high}`}`).join(", ");
  return (
    <>
      {admin ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setEdit(null)}>Add rule</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No cross-validation rules" text="Every combination of valid values is allowed." />}
          columns={[
            { label: "Name", render: (r) => r.name },
            { label: "When", render: (r) => fmt(r.conditions) },
            { label: "Must have", render: (r) => fmt(r.validations) },
            { label: "Status", render: (r) => <Badge status={r.status} /> },
            {
              label: "",
              render: (r) =>
                admin ? (
                  <span style={{ whiteSpace: "nowrap" }}>
                    <button type="button" style={linkBtn} onClick={() => setEdit(r)}>
                      Edit
                    </button>{" "}
                    <button type="button" style={linkBtn} onClick={async () => (await act(() => finService.saveCvr(structure.id, r.id, { ...r, status: r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }))) && reload()}>
                      {r.status === "ACTIVE" ? "Deactivate" : "Activate"}
                    </button>
                  </span>
                ) : null,
            },
          ]}
        />
      </LoadState>
      {edit !== undefined ? <CvrEditor structure={structure} rule={edit} onHide={() => setEdit(undefined)} onDone={reload} /> : null}
    </>
  );
}

/* ------------------------------ page ------------------------------ */

export default function ChartOfAccounts() {
  const gl = useGlContext();
  const { access } = useModuleAccess();
  const admin = isModuleAdmin(access, "FIN");
  const [tab, setTab] = useState("values");
  return (
    <div style={pageStyle}>
      <PageHeader title="Chart of Accounts" subtitle="Accounts, cost centres and the other segments every posting carries" />
      <GlState gl={gl}>
        <LedgerBar gl={gl} />
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "values", label: "Segment values" },
            { key: "structure", label: "Structure" },
            { key: "combinations", label: "Combinations" },
            { key: "cvr", label: "Cross-validation" },
            ...(admin ? [{ key: "lists", label: "Lists of values" }] : []),
          ]}
        />
        {tab === "values" ? <SegmentValues structure={gl.structure} admin={admin} /> : null}
        {tab === "structure" ? <Structure structure={gl.structure} admin={admin} onSaved={gl.reload} /> : null}
        {tab === "combinations" ? <Combinations structure={gl.structure} admin={admin} /> : null}
        {tab === "cvr" ? <CrossValidation structure={gl.structure} admin={admin} /> : null}
        {tab === "lists" ? (
          <MasterSection
            load={() => finService.listMaster("value-sets")}
            save={(id, data) => finService.saveMaster("value-sets", id, data)}
            noun="list of values"
            onChanged={gl.reload}
            columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Max length", (r) => r.max_length]]}
            fieldKeys={["name", "max_length"]}
            lockedOnEdit={["code"]}
            defaults={{ max_length: 10 }}
            fields={() => [
              { key: "code", label: "Code", required: true, max: 24 },
              { key: "name", label: "Name", required: true, max: 100 },
              { key: "max_length", label: "Maximum value length", type: "number", min: 1, required: true },
            ]}
          />
        ) : null}
      </GlState>
    </div>
  );
}
